/**
 * CSV Import Dialog
 *
 * Modal dialog for importing CSV data into a view.
 * Shows a file picker, previews the first 5 rows, and displays
 * import results (success/error counts) after submission.
 */

import React, { useState, useCallback } from "react";
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  Sheet,
} from "@mui/joy";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import type { BulkOperationResult } from "../../services/viewService";

interface CsvImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<BulkOperationResult>;
}

interface CsvPreview {
  headers: string[];
  rows: string[][];
}

/**
 * Parse CSV text into headers and rows (simple parser for preview).
 * Handles quoted fields containing commas.
 */
function parseCsvText(text: string, maxRows = 5): CsvPreview {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const fields: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (inQuotes) {
        if (char === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ",") {
          fields.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
    }
    fields.push(current.trim());
    return fields;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1, maxRows + 1).map(parseLine);

  return { headers, rows };
}

export default function CsvImportDialog({
  open,
  onClose,
  onImport,
}: CsvImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CsvPreview | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<BulkOperationResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setFile(null);
    setPreview(null);
    setIsImporting(false);
    setResult(null);
    setErrorMessage(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setResult(null);
      setErrorMessage(null);
      const selectedFile = event.target.files?.[0];
      if (!selectedFile) {
        setFile(null);
        setPreview(null);
        return;
      }
      setFile(selectedFile);

      // Read and parse preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (text) {
          setPreview(parseCsvText(text));
        }
      };
      reader.onerror = () => {
        setErrorMessage("Failed to read file");
      };
      reader.readAsText(selectedFile);
    },
    [],
  );

  const handleImport = useCallback(async () => {
    if (!file) return;
    setIsImporting(true);
    setErrorMessage(null);
    try {
      const importResult = await onImport(file);
      setResult(importResult);
    } catch (err: any) {
      setErrorMessage(err?.message || "Import failed");
    } finally {
      setIsImporting(false);
    }
  }, [file, onImport]);

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog variant="outlined" sx={{ minWidth: 500, maxWidth: 700 }}>
        <DialogTitle>
          <FileUploadIcon />
          Import CSV
        </DialogTitle>
        <DialogContent>
          {/* File input */}
          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isImporting}
              style={{ marginBottom: 8 }}
            />
            {file && (
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
          </Box>

          {/* Preview table */}
          {preview && preview.headers.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Preview (first {Math.min(preview.rows.length, 5)} rows)
              </Typography>
              <Sheet
                variant="outlined"
                sx={{ borderRadius: "sm", overflow: "auto", maxHeight: 240 }}
              >
                <Table size="sm" stickyHeader>
                  <thead>
                    <tr>
                      {preview.headers.map((header, i) => (
                        <th key={i} style={{ whiteSpace: "nowrap" }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {row.map((cell, cellIdx) => (
                          <td
                            key={cellIdx}
                            style={{
                              whiteSpace: "nowrap",
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            </Box>
          )}

          {/* Result summary */}
          {result && (
            <Box sx={{ mb: 1 }}>
              <Alert
                color={result.error_count > 0 ? "warning" : "success"}
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography level="title-sm">
                    Import complete: {result.success_count} succeeded,{" "}
                    {result.error_count} failed
                  </Typography>
                </Box>
              </Alert>
              {result.errors.length > 0 && (
                <Sheet
                  variant="soft"
                  color="danger"
                  sx={{
                    p: 1.5,
                    borderRadius: "sm",
                    maxHeight: 120,
                    overflow: "auto",
                  }}
                >
                  {result.errors.map((err, i) => (
                    <Typography
                      key={i}
                      level="body-xs"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {err.index !== undefined ? `Row ${err.index}: ` : ""}
                      {err.error}
                    </Typography>
                  ))}
                </Sheet>
              )}
            </Box>
          )}

          {/* Error message */}
          {errorMessage && (
            <Alert color="danger" sx={{ mb: 1 }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {result ? (
            <Button variant="solid" color="primary" onClick={handleClose}>
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="solid"
                color="primary"
                onClick={handleImport}
                disabled={!file || isImporting}
                loading={isImporting}
                startDecorator={<FileUploadIcon />}
              >
                Import
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={handleClose}
                disabled={isImporting}
              >
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
